'use client';

import { Button, Icon } from '@theme/components';
import { useLocalization, useRouter } from '@akinon/next/hooks';
import { useAppSelector } from '@akinon/next/redux/hooks';
import { useEffect, useMemo, useTransition } from 'react';
import { FilterItem } from './filter-item';
import { Drawer } from '@theme/components/drawer';
import { Accordion } from '@theme/components/shadcn/accordion';
import { usePathname } from 'next/navigation';
import { useGroupedFacets } from '../facet-grouping';
import { FacetChoice } from '@akinon/next/types';
import { Facet } from '@theme/types';
import clsx from 'clsx';

interface Props {
  isMenuOpen: boolean;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  categoryPk?: number;
  tempSearchParams: URLSearchParams;
  setTempSearchParams: (params: URLSearchParams) => void;
  totalCount?: number;
}

const PRIORITIZED_BRAND = 'yatsan';

/** Marka filtresinde Yatsan her zaman listenin başında görünür. */
const withPrioritizedBrands = (facet: Facet): Facet => {
  if (facet?.key !== 'brand') {
    return facet;
  }

  const isPrioritized = (choice: FacetChoice) =>
    choice?.label?.toLowerCase() === PRIORITIZED_BRAND;

  return {
    ...facet,
    data: {
      ...facet.data,
      choices: [
        ...facet.data.choices.filter(isPrioritized),
        ...facet.data.choices.filter((choice) => !isPrioritized(choice))
      ]
    }
  };
};

export const FiltersHeader = ({
  setIsMenuOpen
}: {
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  isMenuOpen: boolean;
}) => {
  return (
    <div className="flex items-center text-primary-black py-3 px-5 border-b border-[#CDCFCE]">
      <span className="text-xl font-medium">Filtreler</span>
      <button
        type="button"
        className="ml-auto size-6 flex items-center justify-center"
        onClick={() => setIsMenuOpen(false)}
      >
        <Icon name="y-close" size={13} />
      </button>
    </div>
  );
};

export const DesktopFilters = (props: Props) => {
  const { selectedFacets } = useAppSelector((state) => state.category);
  const { isMenuOpen, tempSearchParams, setTempSearchParams } = props;
  const router = useRouter();
  const pathname = usePathname();

  const selectedKeys = useMemo(() => {
    return selectedFacets.map((facet) => facet.key);
  }, [selectedFacets]);

  const [isPending] = useTransition();

  useEffect(() => {
    router.push(pathname + '?' + tempSearchParams?.toString(), {
      scroll: false
    });
  }, [tempSearchParams]);

  const newFacets = useGroupedFacets();

  return (
    <div
      className={clsx([
        'min-w-[16.25rem] transform transition-all visible',
        !isMenuOpen && '-ml-[300px] invisible opacity-0',
        isMenuOpen && '-ml-0 opacity-100'
      ])}
    >
      <Accordion type="multiple" className="grow" defaultValue={selectedKeys}>
        {newFacets.map((facet) => (
          <FilterItem
            key={facet.key}
            facet={withPrioritizedBrands(facet)}
            isPending={isPending}
            tempSearchParams={tempSearchParams}
            setTempSearchParams={setTempSearchParams}
          />
        ))}
      </Accordion>
    </div>
  );
};

export const MobileFilters = (props: Props) => {
  const { selectedFacets } = useAppSelector((state) => state.category);
  const { t } = useLocalization();
  const router = useRouter();
  const pathname = usePathname();
  const {
    isMenuOpen,
    setIsMenuOpen,
    totalCount,
    tempSearchParams,
    setTempSearchParams
  } = props;

  const [isPending] = useTransition();

  const applyFilters = () => {
    router.push(pathname + '?' + tempSearchParams.toString());
    setIsMenuOpen(false);
  };

  const selectedKeys = useMemo(() => {
    return selectedFacets.map((facet) => facet.key);
  }, [selectedFacets]);

  const newFacets = useGroupedFacets();

  return (
    <Drawer
      portalId="drawer-root"
      open={isMenuOpen}
      onClose={() => setIsMenuOpen(false)}
      direction="right"
      HeaderComponent={
        <FiltersHeader isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      }
    >
      <Accordion
        type="multiple"
        className="p-5 grow"
        defaultValue={selectedKeys}
      >
        {newFacets.map((facet) => (
          <FilterItem
            key={facet.key}
            facet={withPrioritizedBrands(facet)}
            isPending={isPending}
            tempSearchParams={tempSearchParams}
            setTempSearchParams={setTempSearchParams}
            immediateApply
          />
        ))}
      </Accordion>
      <div className="py-4 px-2.5 border-t border-[#CDCFCE]">
        <Button
          appearance="ghost"
          onClick={applyFilters}
          className="h-11 bg-primary-black rounded-12.5 flex w-full text-white text-sm justify-center items-center"
        >
          {t('category.filters.apply')}{' '}
          <span className="ml-1">( {totalCount} ürün )</span>
        </Button>
      </div>
    </Drawer>
  );
};
